import { type CatalogueRegionId, isCatalogueRegionId } from "@shared/types/admin-catalogue";
import type { ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ACTIVE_STATUS_FILTER, CATALOGUE_FIELDS, type CatalogueField, toFilterSelection } from "../admin/admin-catalogue-rows";
import { ADMIN_PRODUCTS_COLUMN_IDS } from "../admin/admin-products-columns";
import { ADMIN_WEB_STATUSES } from "../admin/admin-web-status";
import { ADMIN_PRODUCTS_DEFAULT_PAGE_SIZE, ADMIN_PRODUCTS_PAGE_SIZE_OPTIONS } from "./admin-products-view.utils";

export const ADMIN_PRODUCTS_URL_PARAMS = {
  REGION: "region",
  SEARCH: "q",
  STATUS: "status",
  BADGE: "badge",
  MISSING: "missing",
  SORT: "sort",
  PAGE: "page",
  SIZE: "size",
} as const;

const SORT_SEPARATOR = ":";
const SORT_LIST_SEPARATOR = ",";

const STATUS_VALUES: ReadonlySet<string> = new Set([ACTIVE_STATUS_FILTER, ...ADMIN_WEB_STATUSES]);
const SORTABLE_COLUMN_IDS: readonly string[] = Object.values(ADMIN_PRODUCTS_COLUMN_IDS);

const parsePositiveInt = (raw: string | null): number | undefined => {
  const value = Number(raw);
  return raw !== null && Number.isInteger(value) && value > 0 ? value : undefined;
};

// Several columns are comma-separated in priority order: `sort=roaster:desc,product:asc`.
const parseSorting = (raw: string | null): SortingState =>
  (raw ?? "").split(SORT_LIST_SEPARATOR).flatMap((entry) => {
    const [id, direction] = entry.split(SORT_SEPARATOR);
    return id && SORTABLE_COLUMN_IDS.includes(id) ? [{ id, desc: direction === "desc" }] : [];
  });

const parseColumnFilters = (params: URLSearchParams): ColumnFiltersState => {
  // The status dropdown is single-choice, so only the first value counts.
  const status = params.getAll(ADMIN_PRODUCTS_URL_PARAMS.STATUS).find((value) => STATUS_VALUES.has(value));
  const badge = params.getAll(ADMIN_PRODUCTS_URL_PARAMS.BADGE)[0];
  return [
    ...(status ? [{ id: ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS, value: [status] }] : []),
    ...(badge ? [{ id: ADMIN_PRODUCTS_COLUMN_IDS.BADGES, value: [badge] }] : []),
  ];
};

const isCatalogueField = (value: string): value is CatalogueField => (CATALOGUE_FIELDS as readonly string[]).includes(value);

export interface AdminProductsUrlState {
  search: string;
  columnFilters: ColumnFiltersState;
  missing: CatalogueField[];
  sorting: SortingState;
  pagination: PaginationState;
}

export type AdminProductsUrlStatePatch =
  | Partial<AdminProductsUrlState>
  | ((current: AdminProductsUrlState) => Partial<AdminProductsUrlState>);

/** Reads the catalogue's view state from a query string; invalid values fall back to defaults. */
export const parseAdminProductsUrlState = (params: URLSearchParams): AdminProductsUrlState => {
  const size = parsePositiveInt(params.get(ADMIN_PRODUCTS_URL_PARAMS.SIZE));
  const page = parsePositiveInt(params.get(ADMIN_PRODUCTS_URL_PARAMS.PAGE));
  return {
    search: params.get(ADMIN_PRODUCTS_URL_PARAMS.SEARCH) ?? "",
    columnFilters: parseColumnFilters(params),
    missing: Array.from(new Set(params.getAll(ADMIN_PRODUCTS_URL_PARAMS.MISSING).filter(isCatalogueField))),
    sorting: parseSorting(params.get(ADMIN_PRODUCTS_URL_PARAMS.SORT)),
    pagination: {
      pageIndex: (page ?? 1) - 1,
      pageSize: size !== undefined && ADMIN_PRODUCTS_PAGE_SIZE_OPTIONS.includes(size) ? size : ADMIN_PRODUCTS_DEFAULT_PAGE_SIZE,
    },
  };
};

export const parseAdminProductsRegion = (params: URLSearchParams, fallback: CatalogueRegionId): CatalogueRegionId => {
  const raw = params.get(ADMIN_PRODUCTS_URL_PARAMS.REGION);
  return isCatalogueRegionId(raw) ? raw : fallback;
};

const setOrDelete = (params: URLSearchParams, key: string, value: string | undefined) => {
  if (value === undefined || value === "") params.delete(key);
  else params.set(key, value);
};

const setAll = (params: URLSearchParams, key: string, values: readonly string[]) => {
  params.delete(key);
  values.forEach((value) => params.append(key, value));
};

/** Writes the view state into `params`, omitting anything that equals its default. */
export const writeAdminProductsUrlState = (params: URLSearchParams, state: AdminProductsUrlState): URLSearchParams => {
  const { PAGE, SIZE, SEARCH, STATUS, BADGE, MISSING, SORT } = ADMIN_PRODUCTS_URL_PARAMS;
  const filterValues = (columnId: string): string[] =>
    toFilterSelection(state.columnFilters.find((filter) => filter.id === columnId)?.value);

  setOrDelete(params, SEARCH, state.search.trim() === "" ? undefined : state.search);
  setAll(params, STATUS, filterValues(ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS).slice(0, 1));
  setAll(params, BADGE, filterValues(ADMIN_PRODUCTS_COLUMN_IDS.BADGES).slice(0, 1));
  setAll(params, MISSING, state.missing);
  setOrDelete(
    params,
    SORT,
    state.sorting.map((sort) => `${sort.id}${SORT_SEPARATOR}${sort.desc ? "desc" : "asc"}`).join(SORT_LIST_SEPARATOR)
  );
  setOrDelete(params, PAGE, state.pagination.pageIndex > 0 ? String(state.pagination.pageIndex + 1) : undefined);
  setOrDelete(params, SIZE, state.pagination.pageSize === ADMIN_PRODUCTS_DEFAULT_PAGE_SIZE ? undefined : String(state.pagination.pageSize));
  return params;
};

/**
 * Applies a region change: the badge selection is region-specific, so it is
 * dropped, and the page returns to the first.
 */
export const writeAdminProductsRegion = (params: URLSearchParams, region: CatalogueRegionId): URLSearchParams => {
  params.set(ADMIN_PRODUCTS_URL_PARAMS.REGION, region);
  params.delete(ADMIN_PRODUCTS_URL_PARAMS.BADGE);
  params.delete(ADMIN_PRODUCTS_URL_PARAMS.PAGE);
  return params;
};

/** The region lives in the URL so it survives a refresh and travels with a shared link. */
export const useAdminProductsRegion = (fallback: CatalogueRegionId) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const region = parseAdminProductsRegion(searchParams, fallback);
  const setRegion = useCallback(
    (next: CatalogueRegionId) => setSearchParams((prev) => writeAdminProductsRegion(new URLSearchParams(prev), next), { replace: true }),
    [setSearchParams]
  );
  return [region, setRegion] as const;
};

/** Search, filters, sort and pagination mirrored into the query string (history is replaced, not pushed). */
export const useAdminProductsUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // TanStack Table resets the page index whenever the filter or sort state
  // changes identity, so everything but the page is parsed from a key that
  // ignores it and keeps its references across page changes.
  const withoutPage = new URLSearchParams(searchParams);
  withoutPage.delete(ADMIN_PRODUCTS_URL_PARAMS.PAGE);
  const baseKey = withoutPage.toString();
  const base = useMemo(() => parseAdminProductsUrlState(new URLSearchParams(baseKey)), [baseKey]);
  const { pageIndex } = parseAdminProductsUrlState(searchParams).pagination;
  const state = useMemo(() => ({ ...base, pagination: { pageIndex, pageSize: base.pagination.pageSize } }), [base, pageIndex]);
  // Several changes can land in one tick (clearing filters resets the columns and the
  // "missing" group together), so updates compose over the latest written state
  // instead of the one captured at render.
  const latestRef = useRef(state);
  useEffect(() => {
    latestRef.current = state;
  }, [state]);
  const update = useCallback(
    (patch: AdminProductsUrlStatePatch) => {
      const next = { ...latestRef.current, ...(typeof patch === "function" ? patch(latestRef.current) : patch) };
      latestRef.current = next;
      setSearchParams((prev) => writeAdminProductsUrlState(new URLSearchParams(prev), next), { replace: true });
    },
    [setSearchParams]
  );
  return [state, update] as const;
};
